import React, { useEffect, useState } from 'react';
import { META_AD_TYPE, META_BILLING_EVENT_TYPE, META_CONVERSION_EVENT_TYPE, META_CUSTOM_EVENT_TYPE, META_OPTIMIZATION_GOAL_TYPE } from '../SocialMediaConstant';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import TooltipInfoCard from '../../../components/TooltipInfoCard';
import FacebookFeedAd from './FacebookFeedAd';
import InstagramFeedAd from './InstagramFeedAd';
import InstagramStoriesAd from './InstagramStoriesAd';
import { ADS_TYPE, ENTITYNAME } from '../../../framework/constant/constant';
import * as API from '../../../framework/API/api'

const MetaAdsetForm = ({ dataItem = {}, setDataItem, onChange, objective }) => {

    const ALLOWED_OPTIMIZATION_GOALS_BY_OBJECTIVE = {
        OUTCOME_TRAFFIC: [
            "LINK_CLICKS",
            "LANDING_PAGE_VIEWS",
            "IMPRESSIONS",
            "REACH"
        ],
        OUTCOME_LEADS: [
            "LEAD_GENERATION",
            "CONVERSIONS",
            "MESSAGING_PURCHASE_CONVERSION",
            "REPLIES",
            "QUALITY_CALL"
        ],
        OUTCOME_APP_PROMOTION: [
            "APP_INSTALLS",
            "LINK_CLICKS"
        ],
        OUTCOME_SALES: [
            "CONVERSIONS",
            "OFFSITE_CONVERSIONS",
            "PRODUCT_CATALOG_SALES",
            "VALUE"
        ],
        OUTCOME_ENGAGEMENT: [
            "POST_ENGAGEMENT",
            "PAGE_LIKES",
            "EVENT_RESPONSES"
        ],
        OUTCOME_AWARENESS: [
            "AD_RECALL_LIFT",
            "REACH",
            "IMPRESSIONS"
        ],
        OUTCOME_VIDEO_VIEWS: [
            "THRUPLAY"
        ],
        OUTCOME_LOCAL_AWARENESS: [
            "REACH"
        ],
        OUTCOME_REACH: [
            "REACH"
        ]
    };

    const [billingEvents, setBillingEvents] = useState([]);
    const [optimizationGoals, setOptimizationGoals] = useState([]);
    const [customEventTypes, setCustomEventTypes] = useState([]);
    const [conversionEvents, setConversionEvents] = useState([]);
    const [buttonTypes, setButtonTypes] = useState([]);
    const [pages, setPages] = useState([]);

    useEffect(() => {
        loadCombo();
    }, [])

    const loadCombo = async () => {
        const billingEventRes = Object.keys(META_BILLING_EVENT_TYPE).map(key => ({
            _id: META_BILLING_EVENT_TYPE[key],
            description: key
        }));
        setBillingEvents(billingEventRes);
        const selectedObjective = objective?._id;
        const allowedGoalKeys = ALLOWED_OPTIMIZATION_GOALS_BY_OBJECTIVE[selectedObjective] || [];
        const optimizationGoalRes = Object.entries(META_OPTIMIZATION_GOAL_TYPE)
            .filter(([key, value]) => allowedGoalKeys.includes(key))
            .map(([key, value]) => ({
                _id: value,
                description: key
            }));
        setOptimizationGoals(optimizationGoalRes);

        const customEventTypeRes = Object.keys(META_CUSTOM_EVENT_TYPE).map(key => ({
            _id: META_CUSTOM_EVENT_TYPE[key],
            description: key
        }));
        setCustomEventTypes(customEventTypeRes);
        const conversionEventRes = Object.keys(META_CONVERSION_EVENT_TYPE).map(key => ({
            _id: META_CONVERSION_EVENT_TYPE[key],
            description: key
        }))
        setConversionEvents(conversionEventRes);
        const buttonTypeRes = Object.keys(META_AD_TYPE).map(key => ({
            _id: META_AD_TYPE[key],
            description: key
        }));
        setButtonTypes(buttonTypeRes);
        let channelRes = await API.getDataLookup(ENTITYNAME.SM_Channels);
        let channel = channelRes.data.find(x => x.adType === ADS_TYPE.META);
        let pageRes = await API.getMetaPages({ adsConfig: { ...channel }, fields: "id,name" });
        setPages(pageRes.data);
        if (dataItem?.page_id) {
            let page = pageRes?.data?.find(x => x?.id === dataItem?.page_id) ?? {};
            setDataItem({ ...dataItem, page });
        }
    }

    const hyperlink = (value) => {
        return (<a onClick={() => window.open(value, "_blank")} style={{ cursor: "pointer", color: "blue" }}>{value}</a>)
    }

    return (
        <>
            <div className="row" style={{ height: "390px" }}>
                <div className="col-6">
                    <div className="row">
                        <div className="col-6">
                            <label htmlFor="conversion_location" > Conversion Location *  </label>
                            <TooltipInfoCard
                                message="Choose where you want to generate leads."
                                link="https://www.facebook.com/business/help/2035196646663270"
                                showLearnMore={true}
                                learnMore="Learn More"
                            />
                            <DropDownList
                                name="conversion_location"
                                textField="description"
                                dataItemKey="_id"
                                data={conversionEvents}
                                value={dataItem.conversion_location}
                                onChange={onChange}
                            />
                        </div>
                        <div className="col-6">
                            <div className="flex items-center gap-1">
                                <label htmlFor="billing_event" >Billing Event *</label>
                                <TooltipInfoCard message=" The billing event tells Meta when you get charged during the ad delivery process — it's set at the Ad Set level and depends on your campaign objective." />
                            </div>
                            <DropDownList
                                name="billing_event"
                                textField="description"
                                dataItemKey="_id"
                                data={billingEvents}
                                value={dataItem.billing_event}
                                onChange={onChange}
                            />
                        </div>
                    </div>
                    <div className='row'>
                        <div className="col-6 mt-2">
                            <div className="flex items-center gap-1">
                                <label htmlFor="optimization_goal" >Optimization Goal *</label>
                                <TooltipInfoCard
                                    message={`The Optimization Goal tells Meta what result you care most about, so it
                                    can show your ad to people most likely to take that action.
                                    Leads: Leads, Conversion Leads, Link Clicks
                                    App Installs: App Installs, App Events
                                    Sales/Conversions: Conversions, Value, Purchase`}
                                />
                            </div>
                            <DropDownList
                                name="optimization_goal"
                                textField="description"
                                dataItemKey="_id"
                                data={optimizationGoals}
                                value={dataItem.optimization_goal}
                                onChange={onChange}
                            />
                        </div>
                        <div className="col-6 mt-2">
                            <label htmlFor="pagd" >Page *</label>
                            <DropDownList
                                name="page"
                                textField="name"
                                dataItemKey="id"
                                data={pages}
                                value={dataItem.page}
                                onChange={onChange}
                            />
                        </div>
                        {dataItem.optimization_goal?.description === META_OPTIMIZATION_GOAL_TYPE.APP_INSTALLS &&
                            <div className="col-6 mt-2">
                                <label htmlFor="application_id" >Application Id *</label>
                                <input
                                    type="text"
                                    name="application_id"
                                    placeholder="Enter Application Id"
                                    className="form-control form-control-sm"
                                    value={dataItem.application_id}
                                    onChange={onChange}
                                />
                                <p>{hyperlink("https://developers.facebook.com/apps")} - App ID is displayed at the top.</p>
                            </div>
                        }
                        {dataItem.optimization_goal?.description === META_OPTIMIZATION_GOAL_TYPE.APP_INSTALLS &&
                            <div className="col-6 mt-2">
                                <label htmlFor="object_store_url" >Object Store Url</label>
                                <input
                                    type="text"
                                    name="object_store_url"
                                    placeholder="Enter Object Store Url"
                                    className="form-control form-control-sm"
                                    value={dataItem.object_store_url}
                                    onChange={onChange}
                                />
                            </div>
                        }
                        {dataItem.optimization_goal?.description === META_OPTIMIZATION_GOAL_TYPE.CONVERSIONS &&
                            <div className="col-6 mt-2">
                                <label htmlFor="custom_event_type" >Custom Event Type *</label>
                                <DropDownList
                                    name="custom_event_type"
                                    textField="description"
                                    dataItemKey="_id"
                                    data={customEventTypes}
                                    value={dataItem.custom_event_type}
                                    onChange={onChange}
                                />
                            </div>
                        }
                        {dataItem.optimization_goal?.description === META_OPTIMIZATION_GOAL_TYPE.CONVERSIONS &&
                            <div className="col-6 mt-2">
                                <label htmlFor="pixel_id" >Pixel Id *</label>
                                <input
                                    type="text"
                                    name="pixel_id"
                                    placeholder="Enter Pixel Id"
                                    className="form-control form-control-sm"
                                    value={dataItem.pixel_id}
                                    onChange={onChange}
                                />
                                <p>Open Meta Events Manager, select Business Account,
                                    go to Data Sources, click Pixel name, find Pixel ID at the top.
                                </p>
                            </div>
                        }
                        <div className="col-6 mt-2">
                            <label htmlFor="link" >Link *</label>
                            <input
                                type="text"
                                name="link"
                                placeholder="Enter Link"
                                className="form-control form-control-sm"
                                value={dataItem.link}
                                onChange={onChange}
                            />
                        </div>
                        <div className="col-6 mt-2">
                            <label htmlFor="button_type" >Button Type *</label>
                            <DropDownList
                                name="button_type"
                                textField="description"
                                dataItemKey="_id"
                                data={buttonTypes}
                                value={dataItem.button_type}
                                onChange={onChange}
                            />
                        </div>
                        <div className="col-6 mt-2">
                            <label className='text-uppercase' htmlFor="button_type_link" >{dataItem.button_type?.description} Link *</label>
                            <input
                                type="text"
                                name="button_type_link"
                                className="form-control form-control-sm"
                                value={dataItem.button_type_link}
                                onChange={onChange}
                            />
                        </div>
                        <div className="col-12 mt-2">
                            <label htmlFor="message" >Message *</label>
                            <textarea
                                type="text"
                                name="message"
                                className="form-control form-control-sm"
                                value={dataItem.message}
                                onChange={onChange}
                                placeholder="Enter Message"
                            />
                        </div>
                    </div>
                </div>
                <div className="col-6 m-0 p-0 pr-3" style={{ overflowY: "auto", height: "100%" }}>
                    <div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                            <FacebookFeedAd image={dataItem?.ad?.creatives?.length > 0 ? dataItem?.ad?.creatives[0]?.url : 'https://i.postimg.cc/j5PsF5jZ/defaultmovie.png'} message={dataItem.message} buttonType={dataItem.button_type.description} link={dataItem.link} />
                            <div className='m-2'></div>
                            <InstagramFeedAd image={dataItem?.ad?.creatives?.length > 0 ? dataItem?.ad?.creatives[0]?.url : 'https://i.postimg.cc/j5PsF5jZ/defaultmovie.png'} message={dataItem.message} buttonType={dataItem.button_type.description} />
                        </div>
                    </div>
                    <div className='m-2'></div>
                    <div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                            <InstagramStoriesAd image={dataItem?.ad?.creatives?.length > 0 ? dataItem?.ad?.creatives[0]?.url : 'https://i.postimg.cc/j5PsF5jZ/defaultmovie.png'} message={dataItem.message} buttonType={dataItem.button_type.description} link={dataItem.link} website="automateb.com" />
                            <div className='m-2'></div>
                        </div>
                    </div>
                </div>
            </div>

        </>
    )
}

export default MetaAdsetForm
